/**
 * Real-time Chat Component for Teleconsultation
 * WebSocket-based chat during video consultations
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Send, Paperclip, Image, File, X, MessageSquare,
  ChevronDown, User, Check, CheckCheck, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeleconsultationChat = ({ roomId, isOpen, onClose, onToggle }) => {
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(null);
  const [users, setUsers] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isSending, setIsSending] = useState(false);
  
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  // Connect to WebSocket
  useEffect(() => {
    if (!roomId || !token) return;

    const wsUrl = `${API_URL.replace('https://', 'wss://').replace('http://', 'ws://')}/ws/chat/${roomId}?token=${token}`;
    
    const connect = () => {
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('Chat connected');
        setIsConnected(true);
      };
      
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        handleMessage(data);
      };
      
      ws.onclose = () => {
        console.log('Chat disconnected');
        setIsConnected(false);
        // Reconnect after 3 seconds
        setTimeout(connect, 3000);
      };
      
      ws.onerror = (error) => {
        console.error('Chat error:', error);
      };
      
      wsRef.current = ws;
    };

    connect();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [roomId, token]);

  // Handle incoming messages
  const handleMessage = useCallback((data) => {
    switch (data.type) {
      case 'history':
        setMessages(data.messages || []);
        break;
      case 'message':
        setMessages(prev => [...prev, data]);
        if (!isOpen) {
          setUnreadCount(prev => prev + 1);
        }
        break;
      case 'users':
        setUsers(data.users || []);
        break;
      case 'typing':
        if (data.is_typing) {
          setIsTyping(data.user_name);
        } else {
          setIsTyping(null);
        }
        break;
      case 'system':
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          type: 'system',
          content: data.content,
          timestamp: data.timestamp
        }]);
        break;
      default:
        console.log('Unknown message type:', data.type);
    }
  }, [isOpen]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Reset unread count when opened
  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
      // Send read receipt
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'read' }));
      }
    }
  }, [isOpen]);

  // Send message
  const sendMessage = () => {
    if (!newMessage.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    
    setIsSending(true);
    
    wsRef.current.send(JSON.stringify({
      type: 'message',
      content: newMessage.trim(),
      message_type: 'text'
    }));
    
    setNewMessage('');
    setIsSending(false);
  };

  // Handle typing indicator
  const handleTyping = () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    
    wsRef.current.send(JSON.stringify({
      type: 'typing',
      is_typing: true
    }));
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Stop typing after 2 seconds
    typingTimeoutRef.current = setTimeout(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'typing',
          is_typing: false
        }));
      }
    }, 2000);
  };

  // Handle file upload
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error(t('teleconsultation.file_too_large'));
      return;
    }
    
    // For now, just show a message - actual file sharing would need backend support
    toast.info(t('teleconsultation.attach_file'));
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  // Get initials
  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  // Render message
  const renderMessage = (message) => {
    if (message.type === 'system') {
      return (
        <div key={message.id} className="flex justify-center my-2">
          <span className="text-xs text-gray-500 bg-gray-100 dark:bg-slate-700 px-3 py-1 rounded-full">
            {message.content}
          </span>
        </div>
      );
    }
    
    const isOwn = message.sender_id === user?.id;
    
    return (
      <div
        key={message.id}
        className={`flex gap-2 mb-3 ${isOwn ? 'flex-row-reverse' : ''}`}
      >
        {!isOwn && (
          <Avatar className="w-8 h-8 flex-shrink-0">
            <AvatarFallback className="text-xs">
              {getInitials(message.sender_name)}
            </AvatarFallback>
          </Avatar>
        )}
        
        <div className={`max-w-[75%] ${isOwn ? 'items-end' : 'items-start'}`}>
          {!isOwn && (
            <span className="text-xs text-gray-500 mb-1 block">
              {message.sender_name}
            </span>
          )}
          
          <div
            className={`rounded-2xl px-4 py-2 ${
              isOwn
                ? 'bg-blue-500 text-white rounded-tr-sm'
                : isDarkMode
                ? 'bg-slate-700 text-white rounded-tl-sm'
                : 'bg-gray-100 text-gray-900 rounded-tl-sm'
            }`}
          >
            <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
          </div>
          
          <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : ''}`}>
            <span className="text-xs text-gray-400">{formatTime(message.timestamp)}</span>
            {isOwn && (
              message.read_by?.length > 1 
                ? <CheckCheck className="w-3 h-3 text-blue-400" />
                : <Check className="w-3 h-3 text-gray-400" />
            )}
          </div>
        </div>
      </div>
    );
  };

  // Minimized view (toggle button)
  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className={`fixed bottom-24 left-4 z-30 flex items-center gap-2 px-4 py-2 rounded-full shadow-lg transition-all hover:scale-105 ${
          isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-900'
        }`}
        data-testid="chat-toggle"
      >
        <MessageSquare className="w-5 h-5" />
        <span className="text-sm font-medium">{t('teleconsultation.chat')}</span>
        {unreadCount > 0 && (
          <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center">
            {unreadCount}
          </Badge>
        )}
        {!isConnected && (
          <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
        )}
      </button>
    );
  }

  // Full chat panel
  return (
    <div
      className={`fixed bottom-24 left-4 z-30 w-80 h-[500px] flex flex-col rounded-2xl shadow-2xl overflow-hidden ${
        isDarkMode ? 'bg-slate-900 border border-slate-700' : 'bg-white border border-gray-200'
      }`}
      data-testid="teleconsultation-chat"
    >
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 border-b ${
        isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-gray-50'
      }`}>
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-blue-500" />
          <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {t('teleconsultation.chat')}
          </span>
          {isConnected ? (
            <span className="w-2 h-2 bg-green-500 rounded-full" />
          ) : (
            <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {users.length} {t('teleconsultation.participants')}
          </span>
          <button
            onClick={onClose}
            className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-700`}
          >
            <ChevronDown className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <MessageSquare className={`w-12 h-12 mb-3 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Aucun message pour le moment
            </p>
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Commencez la conversation
            </p>
          </div>
        ) : (
          <>
            {messages.map(renderMessage)}
            <div ref={messagesEndRef} />
          </>
        )}
        
        {isTyping && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span className="flex gap-1">
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </span>
            <span>{isTyping} {t('teleconsultation.typing')}</span>
          </div>
        )}
      </ScrollArea>

      {/* Input */}
      <div className={`p-3 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
        <div className="flex items-center gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            className="hidden"
            accept="image/*,.pdf,.doc,.docx"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className={`p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700`}
            title={t('teleconsultation.attach_file')}
          >
            <Paperclip className="w-5 h-5 text-gray-500" />
          </button>
          
          <Input
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              handleTyping();
            }}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder={t('teleconsultation.your_message')}
            className={`flex-1 ${isDarkMode ? 'bg-slate-800 border-slate-600' : ''}`}
            disabled={!isConnected}
          />
          
          <Button
            onClick={sendMessage}
            disabled={!newMessage.trim() || !isConnected || isSending}
            size="icon"
            className="rounded-full"
          >
            {isSending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TeleconsultationChat;
